<template>
  <!-- Alert banner is now handled by parent Profile.vue via @alert event -->
  <div class="form-section settings-block">
    <div class="section-content" style="margin-top: 8px">
      <div class="help-text" style="margin-bottom: 16px">
        {{ s__('Profile|Control which events trigger notifications.') }}
      </div>
      <div
        v-for="eventType in commonEventTypes"
        :key="eventType.value"
        class="notification-preference-row"
      >
        <label class="checkbox-label">
          <input
            type="checkbox"
            :checked="isEventEnabled(eventType.value)"
            :disabled="disabled || autoSaving"
            @change="toggleEvent(eventType.value, $event.target.checked)"
          />
          <span>{{ eventType.label }}</span>
        </label>
      </div>
      <div v-if="!showAdvancedEvents" class="show-more-container">
        <button
          type="button"
          class="show-more-btn"
          :disabled="disabled"
          @click="showAdvancedEvents = true"
        >
          {{ s__('Profile|Show more events') }}
        </button>
      </div>
      <div v-if="showAdvancedEvents">
        <div
          v-for="eventType in advancedEventTypes"
          :key="eventType.value"
          class="notification-preference-row"
        >
          <label class="checkbox-label">
            <input
              type="checkbox"
              :checked="isEventEnabled(eventType.value)"
              :disabled="disabled"
              @change="toggleEvent(eventType.value, $event.target.checked)"
            />
            <span>{{ eventType.label }}</span>
          </label>
        </div>
        <div class="show-more-container">
          <button
            type="button"
            class="show-more-btn"
            :disabled="disabled"
            @click="showAdvancedEvents = false"
          >
            {{ s__('Profile|Show fewer events') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__ } from '@/locale'

const props = defineProps({
  notificationPreferences: {
    type: Object,
    default: () => ({}),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  dmNotificationsEnabled: {
    type: Boolean,
    default: true,
  },
  expandedByDefault: {
    type: Boolean,
    default: false,
  },
  userId: {
    type: String,
    default: null,
  },
})

const UPDATE_PREFERENCES_MUTATION = gql`
  mutation UpdateUserPreferences($userId: ID!, $notificationPreferences: JSON) {
    updateUserPreferences(userId: $userId, notificationPreferences: $notificationPreferences) {
      user {
        id
        notificationPreferences
      }
      errors
    }
  }
`

const { mutate: updatePreferences } = useMutation(UPDATE_PREFERENCES_MUTATION)

const emit = defineEmits(['update:changes', 'saved', 'alert'])

// Common event types shown by default
const commonEventTypes = [
  { value: 'created', label: s__('Profile|Task created') },
  { value: 'status_changed', label: s__('Profile|Status changed') },
  { value: 'completed', label: s__('Profile|Task completed') },
  { value: 'task_priority_updated', label: s__('Profile|Priority updated') },
  { value: 'task_due_at_updated', label: s__('Profile|Due date updated') },
  { value: 'task_assignee_id_updated', label: s__('Profile|Assignee changed') },
  { value: 'comment_added', label: s__('Profile|Comment added') },
  { value: 'user_mentioned', label: s__('Profile|User mentioned') },
]

// Advanced event types shown when "Show more" is clicked
const advancedEventTypes = [
  { value: 'label_attached', label: s__('Profile|Label attached') },
  { value: 'label_detached', label: s__('Profile|Label detached') },
  { value: 'task_title_updated', label: s__('Profile|Title updated') },
  { value: 'task_description_updated', label: s__('Profile|Description updated') },
  { value: 'task_confidential_updated', label: s__('Profile|Confidentiality changed') },
  { value: 'task_archived_updated', label: s__('Profile|Archive status changed') },
  { value: 'task_grade_created', label: s__('Profile|Grade created') },
  { value: 'task_grade_updated', label: s__('Profile|Grade updated') },
  { value: 'task_grade_destroyed', label: s__('Profile|Grade deleted') },
  { value: 'horizontal_relation_created', label: s__('Profile|Task relation created') },
  { value: 'horizontal_relation_updated', label: s__('Profile|Task relation updated') },
  { value: 'horizontal_relation_destroyed', label: s__('Profile|Task relation removed') },
  { value: 'vertical_relation_created', label: s__('Profile|Task hierarchy created') },
  { value: 'vertical_relation_destroyed', label: s__('Profile|Task hierarchy removed') },
  { value: 'attachment_uploaded', label: s__('Profile|Attachment uploaded') },
  { value: 'attachment_destroyed', label: s__('Profile|Attachment removed') },
]

// All event types combined (for reference)
const allEventTypes = [...commonEventTypes, ...advancedEventTypes]

const originalPreferences = ref({})
const pendingChanges = ref({})
const autoSaving = ref(false)
const alert = ref(null)
const autoSaveTimeout = ref(null)
const formInitialized = ref(false)

// Computed property to check if any advanced event is enabled or missing
const hasEnabledAdvancedEvents = computed(() => {
  const prefs = props.notificationPreferences || {}

  return advancedEventTypes.some(event => {
    // Check if the property exists
    if (!prefs.hasOwnProperty(event.value)) {
      // If not found, show the block (user might have enabled it but it's not in stored prefs)
      return true
    }
    const value = prefs[event.value]

    // Check for boolean true, string "true", or number 1
    // JSONB might store booleans as strings
    return (
      value === true || value === 'true' || value === 1 || String(value).toLowerCase() === 'true'
    )
  })
})

// Start with advanced events hidden by default
// Only show them if user clicks "Show More" OR if any advanced event is explicitly enabled (true)
const showAdvancedEvents = ref(false)

// Watch the computed property to auto-expand when advanced events are enabled
watch(
  hasEnabledAdvancedEvents,
  hasEnabled => {
    showAdvancedEvents.value = hasEnabled
  },
  { immediate: true }
)

// Watch for changes in notificationPreferences prop and reset state
watch(
  () => props.notificationPreferences,
  newPrefs => {
    originalPreferences.value = JSON.parse(JSON.stringify(newPrefs || {}))
    pendingChanges.value = {}
    emit('update:changes', {})
    formInitialized.value = true
  },
  { immediate: true, deep: true }
)

// Auto-save when pendingChanges updates
watch(
  pendingChanges,
  changes => {
    emit('update:changes', { ...changes })

    // Only auto-save if form has been initialized and there are changes
    if (!formInitialized.value) return
    if (Object.keys(changes).length === 0) return

    // Debounce auto-save to avoid too many requests
    if (autoSaveTimeout.value) {
      clearTimeout(autoSaveTimeout.value)
    }
    autoSaveTimeout.value = setTimeout(() => {
      autoSaveNotificationPreferences()
    }, 500)
  },
  { deep: true }
)

function isEventEnabled(eventType) {
  // If there's a pending change, use that
  if (pendingChanges.value.hasOwnProperty(eventType)) {
    return pendingChanges.value[eventType] === true
  }
  // Otherwise check the current preferences (defaults to true if not set)
  const preference = props.notificationPreferences[eventType]
  return preference !== false
}

function toggleEvent(eventType, enabled) {
  // If the new value matches the original, remove from pending changes
  const originalValue = originalPreferences.value[eventType]
  const wouldBeDefault = originalValue === undefined && enabled === true

  if (wouldBeDefault || originalValue === enabled) {
    // Remove from pending changes
    const { [eventType]: _, ...rest } = pendingChanges.value
    pendingChanges.value = rest
  } else {
    // Add to pending changes
    pendingChanges.value = {
      ...pendingChanges.value,
      [eventType]: enabled,
    }
  }
}

// Auto-save function for notification preferences
async function autoSaveNotificationPreferences() {
  if (!props.userId || !props.userId.trim() || autoSaving.value || props.disabled) return

  // Only save if form has been initialized
  if (!formInitialized.value) return

  // Only save if there are pending changes
  if (Object.keys(pendingChanges.value).length === 0) return

  autoSaving.value = true
  alert.value = null

  try {
    // Build notification preferences object to send
    // The service merges with existing preferences, so we send only the changes
    // For enabled events (true), we send null to remove the preference (defaults to enabled)
    // For disabled events (false), we send false to explicitly disable
    const notificationPreferencesToSave = {}
    Object.keys(pendingChanges.value).forEach(eventType => {
      const value = pendingChanges.value[eventType]
      if (value === true) {
        // Setting to true means removing the preference (default behavior)
        // Send null to clear it
        notificationPreferencesToSave[eventType] = null
      } else {
        // Setting to false means explicitly disabling
        notificationPreferencesToSave[eventType] = false
      }
    })

    const { data } = await updatePreferences({
      userId: props.userId,
      notificationPreferences:
        Object.keys(notificationPreferencesToSave).length > 0
          ? notificationPreferencesToSave
          : undefined,
    })

    if (data?.updateUserPreferences?.errors?.length > 0) {
      const alertData = {
        type: 'error',
        title: s__('Profile|Error'),
        message: data.updateUserPreferences.errors.join(', '),
      }
      alert.value = alertData
      emit('alert', alertData)
    } else if (data?.updateUserPreferences?.user) {
      const updatedUser = data.updateUserPreferences.user

      // Update original preferences to reflect saved state
      originalPreferences.value = JSON.parse(
        JSON.stringify(updatedUser.notificationPreferences || {})
      )

      // Clear pending changes
      pendingChanges.value = {}

      const alertData = {
        type: 'success',
        title: s__('Profile|Success'),
        message: s__('Profile|Preferences saved successfully!'),
      }
      alert.value = alertData

      // Emit alert event to parent so it can show the banner
      emit('alert', alertData)

      // Emit saved event to parent
      emit('saved', updatedUser.notificationPreferences)
    }
  } catch (err) {
    const alertData = {
      type: 'error',
      title: s__('Profile|Error'),
      message: err.message || s__('Profile|Failed to save preferences'),
    }
    alert.value = alertData
    emit('alert', alertData)
  } finally {
    autoSaving.value = false
  }
}

// Expose method to reset changes (called by parent)
defineExpose({
  resetChanges() {
    pendingChanges.value = {}
    originalPreferences.value = JSON.parse(JSON.stringify(props.notificationPreferences || {}))
  },
  clearAlert() {
    alert.value = null
  },
})
</script>

<style scoped>
.notification-preference-row {
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.notification-preference-row:last-child {
  border-bottom: none;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: normal;
}

.checkbox-label input[type='checkbox'] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.checkbox-label input[type='checkbox']:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.checkbox-label span {
  font-weight: 500;
  color: #333;
}

.help-text {
  margin-top: 4px;
  font-size: 13px;
  color: #666;
}

.show-more-container {
  margin-top: 12px;
  padding-top: 12px;
  padding-bottom: 24px;
}
</style>
